import { useMutation, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import {
  buildDnsOverrideParams,
  dnsOverridesApi,
  type CreateDnsOverridePayload,
  type UpdateDnsOverridePayload,
} from '../../services/api'

/**
 * DNS Overrides are deployment-wide, not org-scoped — there is no
 * `useAuthStore`/`orgId` gate here on purpose. See the `dnsOverrides` key
 * comment in `queryKeys.ts` and docs/superpowers/specs/2026-08-03-dns-overrides-design.md §3.3.
 */

/**
 * Probe that gates the whole feature.
 *
 * `retry: false`: this probe gates all downstream CRUD UI, so the default
 * `retry: 1` would double time-to-first-render on failure. The
 * `reachable: false` case (a successful 200 whose body reports etcd
 * unreachable) has its own Retry button; the query-error branch itself has
 * no UI defined yet — that's the card's responsibility (Task 7).
 */
export function useDnsOverrideStatus() {
  return useQuery({
    queryKey: queryKeys.dnsOverrides.status(),
    queryFn: () => dnsOverridesApi.status(),
    staleTime: 60 * 1000,
    retry: false,
  })
}

/**
 * Paginated list. `enabled` is driven by the status probe so no CRUD request
 * is ever fired at a deployment where the feature is not configured.
 */
export function useDnsOverridesList(
  params: { search?: string; page?: number },
  enabled: boolean
) {
  const queryString = buildDnsOverrideParams(params)
  return useQuery({
    queryKey: queryKeys.dnsOverrides.list(queryString),
    queryFn: () => dnsOverridesApi.list(queryString),
    enabled,
    // Keep the previous page visible while the next one loads instead of
    // collapsing the table to a spinner on every page/search change.
    placeholderData: (previous) => previous,
  })
}

/**
 * Invalidates both `list` and `status`: `status.record_count` goes stale the
 * moment a record is created or deleted, so the probe must be covered by
 * every mutation here, not just the list. This is a deliberate contract, not
 * incidental prefix-matching — do not narrow this to `queryKeys.dnsOverrides.list`.
 */
function invalidateDnsOverrides() {
  queryClient.invalidateQueries({ queryKey: queryKeys.dnsOverrides.all() })
}

export function useCreateDnsOverride() {
  return useMutation({
    mutationFn: (body: CreateDnsOverridePayload) => dnsOverridesApi.create(body),
    onSuccess: invalidateDnsOverrides,
  })
}

export function useUpdateDnsOverride() {
  return useMutation({
    mutationFn: ({ domain, ...body }: { domain: string } & UpdateDnsOverridePayload) =>
      dnsOverridesApi.update(domain, body),
    onSuccess: invalidateDnsOverrides,
  })
}

export function useDeleteDnsOverride() {
  return useMutation({
    mutationFn: (domain: string) => dnsOverridesApi.remove(domain),
    onSuccess: invalidateDnsOverrides,
  })
}
